import isEmpty from 'lodash/isEmpty'
import type { PivotGrandTotalsEnum, PivotMeasureFormat } from '@ztechapps/common.contract'
import type { PivotExpandState, PivotLeafColumn, PivotMatrix, PivotResult } from './types'
import { PivotNegativeNumberFormatEnum } from '@ztechapps/common.contract'
import { escapeHtml, roundNumber } from '@ztechapps/common.utils'
import { PIVOT_GRAND_TOTAL_LABEL, PIVOT_TOTAL_SUFFIX } from './constants'
import { formatPivotValue } from './formatPivotValue'
import { listPivotColumns } from './listPivotColumns'
import { listPivotRows } from './listPivotRows'
import { hasPivotGrandTotalRow } from './pivotGrandTotals.utils'
import { PivotRowKindEnum } from './PivotRowKindEnum'

// the Excel number format of a measure format (cell z, https://docs.sheetjs.com/docs/csf/features/nf)
const toPivotNumberFormat = (format: Required<PivotMeasureFormat>): string => {
  const symbol = format.currencySymbol.replace(/"/g, '')
  let positive = symbol ? `"${symbol}"` : ''
  positive += format.thousandsSeparator ? '#,##0' : '0'
  if (format.decimalPlaces > 0) positive += `.${'0'.repeat(format.decimalPlaces)}`
  if (format.isPercent) positive += '%'
  // `,` and `.` in a format code follow the opening computer's regional settings;
  // the positive;negative sections set the negative display
  if (format.negativeNumberFormat === PivotNegativeNumberFormatEnum.Parentheses) return `${positive};(${positive})`
  return positive
}

/** Flattens a pivot result into an export-ready string and number grid. */
export const buildPivotMatrix = ({
  result,
  expandState,
  showSubtotals,
  showGrandTotals,
  formatValues = false,
}: {
  result: PivotResult
  expandState: PivotExpandState
  showSubtotals: boolean
  showGrandTotals: PivotGrandTotalsEnum
  /** measure cells as the grid's text, for print */
  formatValues?: boolean
}): PivotMatrix => {
  const { headerRows, leafColumns } = listPivotColumns({ result, showSubtotals, showGrandTotals })
  const items = listPivotRows({ result, expandState, showSubtotals })
  const rowFieldCount = result.rowFields.length
  const matrix: PivotMatrix = []

  const pushMeasureCell = (line: PivotMatrix[number], rowKey: string, leaf: PivotLeafColumn): void => {
    const format = result.measureFormats[leaf.measureIndex]
    const value = result.getValue(rowKey, leaf.columnKey, leaf.measureIndex)
    if (formatValues) {
      const text = formatPivotValue(value, format)
      line.push({
        t: 's',
        v: text,
        h: `<div style="text-align:${format.textAlign.toLowerCase()}">${escapeHtml(text)}</div>`,
      })
      return
    }
    if (value === undefined) {
      line.push(format.nullValue)
      return
    }
    line.push({
      t: 'n',
      v: roundNumber(value, format.decimalPlaces + (format.isPercent ? 2 : 0)),
      z: toPivotNumberFormat(format),
    })
  }

  const spanLeft: number[] = leafColumns.map(() => 0)
  headerRows.forEach((cells, rowIndex) => {
    const line: PivotMatrix[number] =
      rowIndex === 0 ? result.rowFields.map((field) => field.field) : Array.from({ length: rowFieldCount }, () => '')
    let cellIndex = 0
    let index = 0
    while (index < leafColumns.length) {
      if (spanLeft[index] > 0) {
        line.push('')
        spanLeft[index] -= 1
        index += 1
        continue
      }
      const cell = cells[cellIndex]
      cellIndex += 1
      line.push(cell.label)
      for (let k = 0; k < cell.colSpan; k++) {
        if (k > 0) line.push('')
        if (cell.rowSpan > 1) spanLeft[index + k] = cell.rowSpan - 1
      }
      index += cell.colSpan
    }
    matrix.push(line)
  })

  items.forEach((item) => {
    const line: PivotMatrix[number] = []
    for (let level = 0; level < rowFieldCount; level++) {
      const node = item.path[level]
      if (!node) {
        line.push('')
      } else if (item.kind === PivotRowKindEnum.Subtotal && level === item.path.length - 1) {
        line.push(`${node.member.label} ${PIVOT_TOTAL_SUFFIX}`)
      } else {
        line.push(node.member.label)
      }
    }
    const rowKey = !isEmpty(item.path) ? item.path[item.path.length - 1].key : result.rowRoot.key
    leafColumns.forEach((leaf) => pushMeasureCell(line, rowKey, leaf))
    matrix.push(line)
  })

  if (hasPivotGrandTotalRow(showGrandTotals) && rowFieldCount > 0) {
    const line: PivotMatrix[number] = [PIVOT_GRAND_TOTAL_LABEL, ...Array.from({ length: rowFieldCount - 1 }, () => '')]
    leafColumns.forEach((leaf) => pushMeasureCell(line, result.rowRoot.key, leaf))
    matrix.push(line)
  }
  return matrix
}
